import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import styled from "styled-components";
import color from "../../constants/color";
import * as S from "./styles";

interface Option {
  label: string;
  value: string;
}

interface Props {
  onChange: (event: React.SyntheticEvent, value: Option[] | null) => void;
  data: Array<Option>;
  label: string;
  limitResult?: number;
  value: Option[];
  defaultValue?: Option[];
  isError?: boolean;
  helperText?: string;
  placeholder?: string;
  isMultiple?: boolean;
  marginRightProps?: number;
  disabled?: boolean;
  borderRadius?: string | undefined;
}

const useStyles = makeStyles({
  root: {
    "& fieldset": {
      borderWidth: "1px",
      borderColor: "#D0D5DD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2D5BFF !important",
    },
    "& input::placeholder": {
      textOverflow: "ellipsis !important",
    },
  },
});

const SelectAutoCompleteMultiple: React.FC<Props> = ({
  onChange,
  data,
  label,
  value,
  defaultValue,
  isError,
  helperText,
  placeholder,
  marginRightProps,
  borderRadius,
  disabled,
}) => {
  const classes = useStyles();
  const [internalOptions, setInternalOptions] = useState<Option[]>([]);

  // Normaliza label para comparação/deduplicação
  const normalizeLabel = (label?: string): string =>
    (label || "").toLowerCase().trim();

  // Deduplica por label (case-insensitive) e preserva opções selecionadas
  const dedupedData: Option[] = useMemo(() => {
    if (!data) return [];

    const byLabel = new Map<string, Option>();
    data.forEach((opt) => {
      byLabel.set(normalizeLabel(opt.label), opt);
    });

    // Garante que selecionadas existam e substituam itens com mesmo label
    if (value && value.length > 0) {
      value.forEach((selectedOption) => {
        byLabel.set(normalizeLabel(selectedOption.label), selectedOption);
      });
    }

    return Array.from(byLabel.values());
  }, [data, value]);

  // Garantir que todos os valores selecionados estejam nas opções disponíveis
  useEffect(() => {
    if (!dedupedData || !value) {
      setInternalOptions(dedupedData || []);
      return;
    }

    const optionsMap = new Map(
      dedupedData.map((option) => [option.value, option])
    );
    const missingOptions: Option[] = [];

    // Verificar se algum valor selecionado não está nas opções
    if (value && value.length > 0) {
      value.forEach((selectedOption) => {
        if (!optionsMap.has(selectedOption.value)) {
          missingOptions.push(selectedOption);
        }
      });
    }

    // Se houver opções ausentes, adicione-as à lista interna
    if (missingOptions.length > 0) {
      setInternalOptions([...dedupedData, ...missingOptions]);
    } else {
      setInternalOptions(dedupedData);
    }
  }, [dedupedData, value]);

  return (
    <S.Container>
      {label && (
        <S.Label color={isError ? "red" : color.black}>{label}</S.Label>
      )}
      <S.AutoCompleteCustom
        multiple
        id="checkboxes-tags-demo"
        options={internalOptions.length > 0 ? internalOptions : dedupedData}
        value={value || []}
        size="small"
        defaultValue={defaultValue}
        onChange={(event, newValue) => {
          onChange(event, newValue as Option[] | null);
        }}
        disabled={disabled}
        filterSelectedOptions
        disableCloseOnSelect
        getOptionLabel={(option) => (option as Option).label}
        isOptionEqualToValue={(option, value) =>
          (option as Option).value === (value as Option).value
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder || ""}
            variant="outlined"
            error={isError}
            classes={{ root: classes.root }}
          />
        )}
        style={{
          color: isError ? "red" : "#D0D5DD",
          backgroundColor: disabled ? "#D0D5DD" : "#ffffff",
          marginRight: marginRightProps,
        }}
        borderRadius={borderRadius}
      />
      {isError && helperText && <S.Helper>{helperText}</S.Helper>}
    </S.Container>
  );
};

export default SelectAutoCompleteMultiple;
